import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { cld } from "@/lib/cld";
import { CalendarCheck } from "lucide-react";

/**
 * Horizontal auto-scrolling photo strip for the home page.
 * Uses CSS marquee animation (no JS interval) for smooth, low-CPU motion.
 * Pauses on hover.
 */
export default function GalleryMarquee() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/gallery").then(r => {
      // pick photos only; cap at 8 for variety
      const photos = (r.data || []).filter(i => i.type === "photo").slice(0, 8);
      setItems(photos);
    }).catch(() => {});
  }, []);

  if (items.length === 0) return null;
  const resolve = (it) => cld(it.url, "card");
  // duplicate items for seamless loop
  const loop = [...items, ...items];

  return (
    <section className="py-12 bg-[#0A0A0A] overflow-hidden border-y border-[#D4AF37]/15" data-testid="home-marquee">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-6 flex items-center justify-between">
        <div className="overline">{t("home.ambiance_overline")}</div>
        <Link to="/gallery" className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] hover:text-[#E5C869]" data-testid="marquee-view-all">
          {t("nav.gallery")} →
        </Link>
      </div>
      <div className="relative group">
        <div className="flex gap-4 marquee-track" style={{ width: "max-content" }}>
          {loop.map((it, idx) => (
            <Link
              key={idx}
              to="/reservation"
              data-testid={`marquee-item-${idx}`}
              className="relative flex-shrink-0 w-[280px] h-[200px] lg:w-[360px] lg:h-[260px] overflow-hidden border border-[#D4AF37]/15 hover:border-[#D4AF37]/60 transition-colors group/card"
            >
              <img
                src={resolve(it)}
                alt={it.alt?.fr || "Le 17"}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110"
              />
              {/* Reserve CTA overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/85 via-[#050505]/40 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 flex items-end justify-center p-6">
                <span className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#050505] px-5 py-2.5 text-[10px] tracking-[0.22em] uppercase font-medium translate-y-2 group-hover/card:translate-y-0 transition-transform duration-500">
                  <CalendarCheck size={12} /> {t("nav.reserve")}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          animation: marquee 60s linear infinite;
        }
        .group:hover .marquee-track {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}
